'use client';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './style.css';
import { Headline } from '../headline/Headline';
import { TimelineProps } from './types';
import clsx from 'clsx';
import { useTranslations } from '@/lib/locale-context';
import { Link } from '../link/Link';

export function Timeline({
  timelineTitle,
  subtitle,
  text,
  endText,
  endLink,
  timelineLinks = [],
  sectionId,
}: TimelineProps) {
  const [activeIndices, setActiveIndices] = useState<number[]>([]);
  const desktopContainerRef = useRef<HTMLDivElement>(null); // For desktop
  const mobileContainerRef = useRef<HTMLDivElement>(null); // For mobile
  const [lastLeftSideValue, setLastLeftSideValue] = useState(false);

  const { dict } = useTranslations();

  // Function to calculate which items are visible in the viewport
  const calculateVisibleIndices = (
    containerRef: React.RefObject<HTMLDivElement | null>,
  ) => {
    if (!containerRef.current) return [];
    const items = Array.from(
      containerRef.current.querySelectorAll('.timeline-item'),
    );
    const visibleIndices: number[] = [];
    const viewportHeight = window.innerHeight;

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      if (rect.top >= 0 && rect.top <= viewportHeight * 0.8) {
        visibleIndices.push(index);
      }
    });

    return visibleIndices;
  };

  // `useLayoutEffect` for initial calculation
  useLayoutEffect(() => {
    const isMobile = window.innerWidth < 768;
    const containerRef = isMobile ? mobileContainerRef : desktopContainerRef;

    // Initial calculation
    const initialIndices = calculateVisibleIndices(containerRef);
    setActiveIndices(initialIndices);

    // Scroll handler to recalculate visible indices
    const handleScroll = () => {
      const visibleIndices = calculateVisibleIndices(containerRef);
      setActiveIndices(visibleIndices);
    };

    // Attach the scroll listener
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const containerRef = isMobile ? mobileContainerRef : desktopContainerRef;
    const threshold = 0.8;
    let lastScrollY = window.scrollY;

    const observer = new IntersectionObserver(
      (entries) => {
        const isScrollingUp = window.scrollY < lastScrollY;
        lastScrollY = window.scrollY;

        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute('data-index')!, 10);

          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            setActiveIndices((prev) => {
              if (!prev.includes(index)) {
                return [...prev, index];
              }
              return prev;
            });
          } else {
            setActiveIndices((prev) => {
              if (isScrollingUp) {
                return prev.filter((i) => i < index);
              }
              return prev.filter((i) => i !== index);
            });
          }
        });
      },
      { threshold },
    );

    const items =
      containerRef.current?.querySelectorAll('.timeline-item') || [];
    items.forEach((item) => observer.observe(item));

    return () => {
      items.forEach((item) => observer.unobserve(item));
    };
  }, []);

  useEffect(() => {
    if (timelineLinks.length > 0) {
      // Get the last item in timelineLinks
      const lastIndex = timelineLinks.length - 1;
      const lastItem = timelineLinks[lastIndex];

      if (lastItem && lastItem.leftSide && lastItem.leftSide.value == null) {
        setLastLeftSideValue(true);
      }
    }
  }, [timelineLinks]);
  return (
    <div
      data-component-id="C025"
      id={sectionId}
      className={clsx(
        'smzh-timeline timeline-container w-full bg-blue',
        {
          'last-left-side-value':
            activeIndices.includes(timelineLinks.length - 1) &&
            lastLeftSideValue,
        }, // Add class when the last leftSide is active and its value is null
      )}
    >
      {/* Desktop Version */}
      <div
        ref={desktopContainerRef}
        className="desktop-timeline container mx-auto hidden h-[100vh] overflow-y-scroll md:block"
      >
        <div className="timeline-headlines relative z-10 mx-auto flex w-[100%] flex-col items-center justify-center bg-blue pt-[80px] text-center">
          {subtitle && (
            <Headline {...subtitle} className="text-xs font-medium text-white">
              {subtitle.title}
            </Headline>
          )}
          <Headline
            {...timelineTitle}
            className="mb-11 text-4xl font-bold text-white"
          >
            {timelineTitle.title}
          </Headline>
        </div>

        {timelineLinks.map((item, index) => (
          <div
            key={index}
            data-index={index}
            className={clsx('timeline-item', {
              active: activeIndices.includes(index),
              '!mb-0 !min-h-[250px]':
                lastLeftSideValue && index === timelineLinks.length - 1, // Apply 'mb-0' if lastLeftSideValue is true and this is the last item
            })}
          >
            {item.leftSide && (
              <div
                className={clsx('timeline-left text-white', {
                  active: activeIndices.includes(index),
                })}
              >
                <div className="text-2xl font-medium">{item.leftSide.key}</div>
                <div className="text-xl font-normal">{item.leftSide.value}</div>
              </div>
            )}
            <div className="timeline-marker"></div>
            {item.rightSide && (
              <div
                className={clsx('timeline-right text-white', {
                  active: activeIndices.includes(index),
                })}
              >
                <div className="text-2xl font-medium">{item.rightSide.key}</div>
                <div className="text-xl font-normal">
                  {item.rightSide.value}
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="timeline-headlines relative z-10 mx-auto flex flex-col items-center justify-center space-y-[24px] bg-blue pb-[80px] text-center">
          <Headline
            level="h3"
            variant="headingThree"
            className="mb-0 w-[50%] !font-normal text-white"
            title={endText}
          >
            {endText}
          </Headline>
          <Link
            title={dict.timeline.link.title}
            href={dict.timeline.link.href}
            variant={dict.timeline.link.variant}
            rightIcon={dict.timeline.link.rightIcon}
            className={dict.timeline.link.className}
          />
        </div>
      </div>
      {/* Mobile Version */}
      <div
        ref={mobileContainerRef}
        className="mobile-timeline mx-auto block h-[100vh] w-full overflow-y-scroll md:hidden md:w-[80%]"
      >
        <div className="timeline-headlines relative z-10 mx-auto flex w-[100%] flex-col items-center justify-center bg-blue pt-[80px] text-center">
          {subtitle && (
            <Headline {...subtitle} className="text-xs font-medium text-white">
              {subtitle.title}
            </Headline>
          )}
          <Headline
            {...timelineTitle}
            className="text-4xl font-bold text-white"
          >
            {timelineTitle.title}
          </Headline>
        </div>

        {timelineLinks.map((item, index) => (
          <React.Fragment key={index}>
            {item.rightSide &&
              (index !== timelineLinks.length - 1 || !lastLeftSideValue ? ( // Exclude the last item if lastLeftSideValue is true
                <div
                  key={`${index}-right`}
                  data-index={`${index * 2}`}
                  className={clsx('timeline-item', {
                    active: activeIndices.includes(index * 2),
                  })}
                >
                  <div className="timeline-right flex flex-col gap-8 text-white">
                    <section>
                      <div className="timeline-marker"></div>
                      <div className="pl-20 text-[22px] font-medium">
                        {item.rightSide.key}
                      </div>
                      <div className="mb-6- pl-20 text-[18px] font-normal">
                        {item.rightSide.value}
                      </div>
                    </section>
                  </div>
                </div>
              ) : null)}
            {item.leftSide &&
              (index !== timelineLinks.length - 1 || !lastLeftSideValue ? ( // Exclude the last item if lastLeftSideValue is true
                <div
                  key={`${index}-left`}
                  data-index={`${index * 2 + 1}`}
                  className={clsx('timeline-item', {
                    active: activeIndices.includes(index * 2 + 1),
                  })}
                >
                  <div className="timeline-right flex flex-col gap-8 text-white">
                    <section>
                      <div className="timeline-marker"></div>
                      <div className="pl-20 text-[22px] font-medium">
                        {item.leftSide.key}
                      </div>
                      <div className="pl-20 text-[18px] font-normal">
                        {item.leftSide.value}
                      </div>
                    </section>
                  </div>
                </div>
              ) : null)}
          </React.Fragment>
        ))}

        <div className="timeline-headlines relative z-10 mx-auto flex flex-col items-center justify-center space-y-[24px] bg-blue pb-[80px] text-center">
          <Headline
            level="h3"
            variant="headingThree"
            className="mb-0 text-white"
          >
            {endText}
          </Headline>
          <Link
            title={dict.timeline.link.title}
            href={dict.timeline.link.href}
            variant={dict.timeline.link.variant}
            rightIcon={dict.timeline.link.rightIcon}
            className={dict.timeline.link.className}
          />
        </div>
      </div>
    </div>
  );
}
